import {
  GENERIC_PAYMENT_ICON_URL,
  normalizeCardBrand as normalizeSharedCardBrand,
} from "@fluid-app/payments-display/brands";
import {
  cardDisplayLabel,
  isCardPaymentDescriptor,
  resolvePaymentMethodDisplay,
} from "@fluid-app/payments-display/display";
import type { CardBrand } from "@fluid-app/payments-display/ports";

export { GENERIC_PAYMENT_ICON_URL };

export type NormalizedCardBrand = CardBrand;

type PaymentMethodDisplayInput = {
  logoUrl?: string | null;
  cardBrand?: string | null;
  cardType?: string | null;
  detailsType?: string | null;
  paymentTitle?: string | null;
  paymentType?: string | null;
  source?: string | null;
};

export function normalizeCardBrand(
  brand: string | null | undefined,
): NormalizedCardBrand | null {
  return normalizeSharedCardBrand(brand);
}

export function resolvePaymentMethodLogoUrl(
  input: PaymentMethodDisplayInput,
): string | null {
  const logoUrl = trimToNull(input.logoUrl);

  if (isCardPaymentMethod(input)) {
    const display = resolvePaymentMethodDisplay({
      kind: "card",
      brand: cardBrandCandidate(input),
      logoUrl,
    });
    return display.logo.primaryUrl;
  }

  return logoUrl;
}

export function formatMaskedLastFour(
  lastFour: string | null | undefined,
  brand?: string | null | undefined,
): string | null {
  const value = trimToNull(lastFour);
  if (!value) return null;

  return cardDisplayLabel(value, labelBrandCandidate(brand));
}

export function resolvePaymentMethodDisplayLabel(
  input: PaymentMethodDisplayInput & {
    lastFour?: string | null;
  },
): string | null {
  const maskedLastFour = formatMaskedLastFour(
    input.lastFour,
    cardBrandCandidate(input),
  );
  if (maskedLastFour) return maskedLastFour;

  const paymentTitle = trimToNull(input.paymentTitle);
  if (paymentTitle) return paymentTitle;

  const paymentType = trimToNull(input.paymentType);
  if (paymentType) return paymentType;

  return null;
}

function isCardPaymentMethod(input: PaymentMethodDisplayInput): boolean {
  return (
    trimToNull(input.cardBrand) != null ||
    trimToNull(input.cardType) != null ||
    isCardPaymentDescriptor(input.detailsType) ||
    isCardPaymentDescriptor(input.paymentTitle) ||
    isCardPaymentDescriptor(input.paymentType) ||
    isCardPaymentDescriptor(input.source)
  );
}

function cardBrandCandidate(input: PaymentMethodDisplayInput): string | null {
  return (
    labelBrandCandidate(input.cardBrand) ?? labelBrandCandidate(input.cardType)
  );
}

function labelBrandCandidate(value: string | null | undefined): string | null {
  const normalized = trimToNull(value);
  if (!normalized || isCardPaymentDescriptor(normalized)) return null;
  return normalizeCardBrand(normalized);
}

function trimToNull(value: string | null | undefined): string | null {
  const trimmed = value?.trim();
  return trimmed ? trimmed : null;
}
